import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import assert from "assert";
import clsx from "clsx";
import { useRef, useContext, useEffect } from "react";
import { Box } from "../Box/Box.mjs";
import { Stack } from "../Stack/Stack.mjs";
import { Text } from "../Text/Text.mjs";
import { FieldOverlay } from "../private/FieldOverlay/FieldOverlay.mjs";
import { StepContext, StepperContext } from "./StepperContext.mjs";
import { step, tone, verticalProgressTrack, progressLine, progressYUnfilled, gridContainer, indicatorContainer, focusOverlay, text, progressTrack, progressXUnfilled, indicator, highlight, complete, active, inner, tick } from "./Stepper.css.mjs";
const StepIndicator = ({ complete: complete$1, started, active: active$1 }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    focusable: "false",
    viewBox: "0 0 20 20",
    className: clsx(
      indicator,
      complete$1 || active$1 || started ? highlight : void 0,
      complete$1 && !active$1 ? complete : void 0,
      active$1 ? active : void 0
    ),
    children: [
      /* @__PURE__ */ jsx("circle", { fill: "none", stroke: "currentColor", strokeWidth: 2, cx: 10, cy: 10, r: 9 }),
      /* @__PURE__ */ jsx("circle", { cx: 10, cy: 10, r: 5, className: inner }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M15.5916 6.0083C15.4858 5.9021 15.3578 5.8216 15.2204 5.7672C15.083 5.7128 14.9384 5.684 14.7916 5.684C14.6447 5.684 14.5 5.7128 14.3626 5.7672C14.2252 5.8216 14.0972 5.9021 13.9914 6.0083L7.8666 12.1333L5.5916 9.8583C5.5106 9.7774 5.408 9.7162 5.2955 9.675C5.183 9.6338 5.0646 9.6131 4.9453 9.614C4.826 9.6148 4.7089 9.6361 4.5988 9.6766C4.4886 9.7171 4.3872 9.7762 4.2996 9.8516C4.212 9.9271 4.1395 10.018 4.086 10.118C4.0324 10.2179 3.9988 10.3258 3.9876 10.4361C3.9764 10.5464 3.9878 10.6576 4.0213 10.7643C4.0548 10.871 4.1095 10.9716 4.1816 11.0616L7.3416 14.2216C7.4455 14.3258 7.5686 14.4044 7.703 14.4516C7.8375 14.4988 7.9799 14.5132 8.1216 14.4949C8.2633 14.4766 8.4016 14.4251 8.5292 14.3433C8.6568 14.2614 8.7706 14.1511 8.862 14.0183L15.5916 7.2883C15.695 7.1848 15.7741 7.0613 15.8224 6.9273C15.8707 6.7933 15.8875 6.6515 15.8716 6.5116C15.8556 6.3717 15.8076 6.2365 15.7316 6.1183C15.6556 6.0002 15.553 5.9026 15.4366 5.8283Z",
          className: tick
        }
      )
    ]
  }
);
const Step = ({
  complete: complete2 = false,
  id,
  label,
  description,
  labelInfo,
  align = "center"
}) => {
  const stepRef = useRef(null);
  const { stepNumber, isLast } = useContext(StepContext);
  const stepperContext = useContext(StepperContext);
  assert(
    stepperContext !== null,
    "A Step must be rendered as a child of a Stepper. See the documentation for correct usage: https://design-system.machobear.ca"
  );
  const {
    focusedStep,
    activeStep,
    tone: tone$1,
    progress,
    isLinear,
    onKeyUp,
    onKeyDown,
    onClick,
    onFocus,
    onBlur,
    onStepClick,
    direction
  } = stepperContext;
  const active2 = activeStep === stepNumber;
  const focused = focusedStep === stepNumber;
  const linearStepBeforeProgress = isLinear && stepNumber < progress;
  const completed = complete2 || linearStepBeforeProgress;
  const started = active2 || complete2 || isLinear && stepNumber <= progress;
  const keyboardAccessible = focused || active2 && focusedStep === null;
  const interactable = typeof onStepClick === "function" && !active2 && (!isLinear || stepNumber <= progress);
  const isVertical = direction === "vertical";
  useEffect(() => {
    if (stepRef.current && focused) {
      stepRef.current.focus();
    }
  }, [focused]);
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "button",
      ref: stepRef,
      position: "relative",
      outline: "none",
      display: "block",
      width: "full",
      role: "button",
      cursor: interactable ? "pointer" : void 0,
      pointerEvents: !interactable && !active2 ? "none" : void 0,
      "aria-current": active2 ? "step" : void 0,
      className: [step, tone[tone$1]],
      onClick: interactable ? () => {
        if (onClick) {
          onClick(stepNumber);
        }
        if (onStepClick) {
          onStepClick({ id, stepNumber });
        }
      } : void 0,
      onKeyUp,
      onKeyDown,
      onFocus,
      onBlur: keyboardAccessible ? onBlur : void 0,
      tabIndex: keyboardAccessible ? 0 : -1,
      paddingBottom: isVertical ? "large" : void 0,
      children: isVertical ? /* @__PURE__ */ jsxs(Fragment, { children: [
        !isLast ? /* @__PURE__ */ jsx(Box, { position: "absolute", overflow: "hidden", className: verticalProgressTrack, children: /* @__PURE__ */ jsx(
          Box,
          {
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: "full",
            className: [progressLine, !completed ? progressYUnfilled : void 0]
          }
        ) }) : null,
        /* @__PURE__ */ jsxs(Box, { className: gridContainer, children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              display: "block",
              position: "relative",
              transition: "fast",
              className: indicatorContainer,
              "aria-hidden": true,
              children: [
                /* @__PURE__ */ jsx(
                  FieldOverlay,
                  {
                    variant: "focus",
                    borderRadius: "full",
                    onlyVisibleForKeyboardNavigation: true,
                    className: focusOverlay
                  }
                ),
                /* @__PURE__ */ jsx(StepIndicator, { complete: completed, active: active2, started })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "row", alignItems: "center", gap: "xxsmall", children: [
            /* @__PURE__ */ jsx(Box, { userSelect: "none", className: !active2 && !isLinear ? text : void 0, children: /* @__PURE__ */ jsx(
              Text,
              {
                align: { mobile: "left", tablet: "left" },
                weight: active2 ? "strong" : void 0,
                tone: !active2 ? "tertiary" : void 0,
                children: label
              }
            ) }),
            labelInfo ? /* @__PURE__ */ jsx(Box, { children: labelInfo }) : null
          ] }),
          /* @__PURE__ */ jsx(Box, {}),
          description ? /* @__PURE__ */ jsx(Box, { userSelect: "none", marginTop: "xxsmall", children: /* @__PURE__ */ jsx(Text, { align: "left", tone: "tertiary", size: "small", children: description }) }) : null
        ] })
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        !isLast ? /* @__PURE__ */ jsx(
          Box,
          {
            component: "span",
            position: "absolute",
            overflow: "hidden",
            className: progressTrack,
            children: /* @__PURE__ */ jsx(
              Box,
              {
                component: "span",
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: "full",
                className: [progressLine, !completed ? progressXUnfilled : void 0]
              }
            )
          }
        ) : null,
        /* @__PURE__ */ jsxs(Stack, { component: "span", space: "medium", align: { mobile: "left", tablet: "center" }, children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              component: "span",
              display: "block",
              position: "relative",
              transition: "fast",
              className: indicatorContainer,
              "aria-hidden": true,
              children: [
                /* @__PURE__ */ jsx(
                  FieldOverlay,
                  {
                    variant: "focus",
                    borderRadius: "full",
                    onlyVisibleForKeyboardNavigation: true,
                    className: focusOverlay
                  }
                ),
                /* @__PURE__ */ jsx(StepIndicator, { complete: completed, active: active2, started })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(Box, { children: [
            /* @__PURE__ */ jsxs(Stack, { space: "xxsmall", align: "center", children: [
              /* @__PURE__ */ jsx(Box, { paddingX: "xsmall", userSelect: "none", children: /* @__PURE__ */ jsx(
                Text,
                {
                  align: { mobile: "left", tablet: align },
                  weight: active2 ? "strong" : void 0,
                  tone: !active2 ? "tertiary" : void 0,
                  children: label
                }
              ) }),
              labelInfo ? /* @__PURE__ */ jsx(Box, { children: labelInfo }) : null
            ] }),
            /* @__PURE__ */ jsx(Box, { paddingX: "xsmall", userSelect: "none", marginTop: "xxsmall", children: description ? /* @__PURE__ */ jsx(Text, { align: { mobile: "left", tablet: align }, tone: "tertiary", size: "small", children: description }) : null })
          ] })
        ] })
      ] })
    }
  );
};
Step.__isStep__ = true;
export {
  Step,
  StepIndicator
};
